import { useEffect, useRef } from "react";

// Types

export interface UseVocabularyKeyboardOptions {
  /** Whether the drawer is currently open (suppresses all keyboard handling) */
  isDrawerOpen: boolean;
}

export interface UseSpaceToAdvanceOptions extends UseVocabularyKeyboardOptions {
  /** Whether the space key should trigger advancement */
  enabled: boolean;
  /** Callback when space is pressed */
  onAdvance: () => void;
}

// Hooks

/**
 * Listens for the spacebar to advance past an expired word.
 *
 * Only active when `enabled` is true and the drawer is closed.
 *
 * Implementation notes:
 * - Uses refs to store current values of `enabled`, `onAdvance`, and `isDrawerOpen`
 *   so the event listener is registered once and reads the latest values at
 *   invocation time. This prevents listener re-registration on prop changes.
 * - The listener is registered once on mount and removed on unmount.
 */
export function useSpaceToAdvance({
  enabled,
  onAdvance,
  isDrawerOpen,
}: UseSpaceToAdvanceOptions): void {
  // Store current values in refs to avoid re-registering the listener
  const enabledRef = useRef(enabled);
  const onAdvanceRef = useRef(onAdvance);
  const isDrawerOpenRef = useRef(isDrawerOpen);

  // Sync refs with latest prop values
  useEffect(() => {
    enabledRef.current = enabled;
    onAdvanceRef.current = onAdvance;
    isDrawerOpenRef.current = isDrawerOpen;
  }, [enabled, onAdvance, isDrawerOpen]);

  // Register listener once on mount
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isDrawerOpenRef.current) return;
      if (enabledRef.current && e.key === " ") {
        e.preventDefault();
        onAdvanceRef.current();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
}
